import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { MenuModule } from 'primeng/menu';
import { FormsModule } from '@angular/forms';
import { CartButtonComponent } from '../cart-button/cart-button.component';
import { ShippingSelectorComponent } from '../shipping-selector/shipping-selector.component';
import { QuantitySelectorComponent } from '../quantity-selector/quantity-selector.component';
import {
  IBenefits,
  ItemBenefitsComponent,
} from '../item-benefits/item-benefits.component';
import {
  AdjustItemQuantityMutation,
  AdjustItemQuantityMutationVariables,
  Discount,
  Order,
  OrderLine,
  Product,
  ProductVariant,
  RemoveItemFromCartMutation,
  RemoveItemFromCartMutationVariables,
} from '../../../../graphql/generated/generated-types';
import { FormatPricePipe } from '../../../pipes/format-price.pipe';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DataService } from '../../../../core/providers/data/data.service';
import {
  ADJUST_ITEM_QUANTITY,
  REMOVE_ITEM_FROM_CART,
} from './product-cart.graphql';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ActiveService } from '../../../../core/providers/active/active.service';
import { Platform } from '@angular/cdk/platform';
import { NotAvailableOverlayComponent } from './../../not-available-overlay/not-available-overlay.component';
import { SavedItemsService } from '../../../services/saved-items.service';
import { environment } from '../../../../../environments/environment';
import { ToastSeverity } from '../../../notification/notification.component';
import { SlugTypes } from '../../../../routes/landing/components/product-categories/product-category-search-block/product-category-search-block.component';
@Component({
  selector: 'app-price-and-actions-block',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MenuModule,
    RippleModule,
    ButtonModule,
    InputNumberModule,
    CartButtonComponent,
    ShippingSelectorComponent,
    QuantitySelectorComponent,
    ItemBenefitsComponent,
    FormatPricePipe,
    ProgressSpinnerModule,
    NotAvailableOverlayComponent,
  ],
  templateUrl: './price-and-actions-block.component.html',
  styleUrl: './price-and-actions-block.component.scss',
})
export class PriceAndActionsBlockComponent implements OnInit, OnDestroy {
  public activeOrder$: Observable<Order>;
  public discounts: Discount[] = [];
  public qtyInCart: { [id: string]: number } = {};
  public orderLineInCart: { [id: string]: OrderLine } = {};

  @Input() product!: Product;
  @Input() productVariant!: ProductVariant;
  @Input() shippingMethods: any;
  @Input() quantity: number | undefined = 1;
  @Input() quantityEnabled: boolean = true;
  @Input() shippingEnabled: boolean = true;
  @Input() benefitsEnabled: boolean = true;
  @Input() selectedAsset: { id: string; preview: string };

  private unsubscribe$ = new Subject<void>();
  public itemBenefits: IBenefits = {
    // yearlyWarranity:2,
    nonReturnable: true,
    shippingWeeksMin: 2,
    shippingWeeksMax: 4,
  };

  public stockLevel!: number;

  public enableSavedItems: boolean = environment.websiteSettings.enableSavedItems;
  public productInSaved?: boolean = false;

  constructor(
    private dataService: DataService,
    private messageService: MessageService,
    private platform: Platform,
    private activeService: ActiveService,
    private savedItemsService: SavedItemsService
  ) { }

  public subscribeToActiveOrder() {
    this.unsubscribe$.next();
    this.activeService.activeOrder$
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((order) => {
        this.qtyInCart = {};
        this.orderLineInCart = {};
        for (const line of order?.lines ?? []) {
          this.qtyInCart[line.productVariant.id] = line.quantity;
          this.orderLineInCart[line.productVariant.id] = line;
        }
      });
  }

  ngOnInit(): void {
    if (this.platform.isBrowser) {
      this.subscribeToActiveOrder();
      this.productInSaved = this.savedItemsService.productInSaved(
        this.product.id
      );
    }

    if (this.productVariant) {
      this.stockLevel = this.convertStringToNumber(
        this.productVariant.stockLevel
      );
    }
  }

  public addItemToSaved() {
    this.savedItemsService.addProductToSavedItems(this.product.id);
    this.productInSaved = this.savedItemsService.productInSaved(
      this.product.id
    );
  }

  public convertStringToNumber(value: string): number {
    return parseFloat(value);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  setQuantity(event: { itemId: string; quantity: number }) {
    this.quantity = event.quantity;

    if (this.qtyInCart[this.productVariant.id]) {
      if (0 < event.quantity) {
        this.adjustItemQuantity(event.itemId, event.quantity);
      } else {
        this.removeItem(event.itemId);
      }
    } else {
    }
  }

  private adjustItemQuantity(id: string, qty: number) {
    this.dataService
      .mutate<AdjustItemQuantityMutation, AdjustItemQuantityMutationVariables>(
        ADJUST_ITEM_QUANTITY,
        {
          id,
          qty,
        }
      )
      .pipe(take(1))
      .subscribe(({ adjustOrderLine }) => {
        switch (adjustOrderLine.__typename) {
          case 'Order':
            break;
          case 'InsufficientStockError':
          case 'NegativeQuantityError':
          case 'OrderLimitError':
          case 'OrderModificationError':
            this.messageService.add({
              severity: ToastSeverity.Error,
              key: 'notifications',
              summary: 'Error',
              detail: `${adjustOrderLine.message}`,
            });
            break;
        }
      });
  }

  private removeItem(id: string) {
    this.dataService
      .mutate<RemoveItemFromCartMutation, RemoveItemFromCartMutationVariables>(
        REMOVE_ITEM_FROM_CART,
        {
          id,
        }
      )
      .pipe(take(1))
      .subscribe();
  }
}
